import React, { useEffect, useState } from 'react';
import { tenantAPI } from '../../lib/api';
import { formatCurrency, formatDate, getStatusColor, downloadBlob } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { FileText, Building2, DoorOpen, Calendar, User, CheckCircle, Loader2, Lock, Shield, CreditCard, ScrollText, Wifi, Droplets, Zap, Tv, WashingMachine, Download } from 'lucide-react';

export default function TenantLease() {
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadLease();
  }, []);

  const loadLease = async () => {
    try {
      const response = await tenantAPI.getLease();
      setLease(response.data);
    } catch (error) {
      console.error('Error loading lease:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await tenantAPI.acknowledgeLease(lease.lease_id);
      toast.success('Lease acknowledged successfully! The lease is now locked.');
      loadLease();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge lease');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await tenantAPI.exportLeasePdf();
      downloadBlob(response.data, `lease_agreement_${lease.lease_id}.pdf`);
      toast.success('Lease PDF downloaded');
    } catch (error) {
      toast.error('Failed to download lease PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lease) {
    return (
      <div className="space-y-6" data-testid="tenant-lease-page">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">My Lease</h1>
          <p className="text-muted-foreground mt-1">View your lease agreement</p>
        </div>
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No Lease Agreement</h3>
            <p className="text-muted-foreground">Your landlord will create a lease agreement for you.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-lease-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">My Lease Agreement</h1>
          <p className="text-muted-foreground mt-1">View your lease details and terms</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={downloading}
            data-testid="download-lease-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
          <Badge className={`${getStatusColor(lease.status)} text-sm px-3 py-1`}>
            {lease.status}
          </Badge>
          {lease.is_locked && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Locked
            </Badge>
          )}
        </div>
      </div>

      {/* Pending Acknowledgement Alert */}
      {lease.status === 'pending' && !lease.tenant_acknowledged && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Action Required</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Please review all terms below carefully and acknowledge to activate your lease.
                  Once acknowledged, the lease will be locked and cannot be changed.
                </p>
              </div>
              <Button
                onClick={handleAcknowledge}
                disabled={acknowledging}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="acknowledge-lease-btn"
              >
                {acknowledging ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                I Agree & Acknowledge
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Force-Activated Lease - Needs Acknowledgement */}
      {lease.status === 'active' && lease.force_activated && !lease.tenant_acknowledged && (
        <Card className="border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300">Acknowledgement Required</h3>
                <p className="text-blue-700 dark:text-blue-400 text-sm mt-1">
                  Your landlord has activated this lease on your behalf. Please review all terms below and acknowledge to lock the lease.
                  The lease is active but will remain unlocked until you acknowledge.
                </p>
                {lease.force_activation_reason && (
                  <p className="text-blue-600 dark:text-blue-400 text-xs mt-2 italic">
                    Landlord's note: "{lease.force_activation_reason}"
                  </p>
                )}
              </div>
              <Button
                onClick={handleAcknowledge}
                disabled={acknowledging}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="acknowledge-force-activated-btn"
              >
                {acknowledging ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                I Agree & Acknowledge
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {lease.tenant_acknowledged && lease.status === 'pending' && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
              <div>
                <h3 className="font-semibold text-emerald-800">Acknowledged & Locked</h3>
                <p className="text-emerald-700 text-sm">
                  You acknowledged this lease on {formatDate(lease.acknowledged_at)}. Waiting for your landlord to activate.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lease Details */}
      <Card>
        <CardHeader>
          <CardTitle>Lease Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Property & Unit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <Building2 className="w-4 h-4" />
                Property
              </div>
              <p className="font-semibold text-lg">{lease.property?.name}</p>
              <p className="text-sm text-muted-foreground">{lease.property?.address}</p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <DoorOpen className="w-4 h-4" />
                Unit
              </div>
              <p className="font-semibold text-lg">Room {lease.unit?.unit_number}</p>
              {lease.unit?.floor && <p className="text-sm text-muted-foreground">{lease.unit.floor} Floor</p>}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Start Date</p>
              <p className="font-semibold">{formatDate(lease.start_date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">End Date</p>
              <p className="font-semibold">{formatDate(lease.end_date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Monthly Rent</p>
              <p className="font-semibold text-[#C85A42] text-xl">{formatCurrency(lease.total_monthly_rent || lease.monthly_rent)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Security Deposit</p>
              <p className="font-semibold">{formatCurrency(lease.deposit_amount)}</p>
            </div>
          </div>

          {/* Rent Breakdown */}
          {lease.additional_fees && (
            <>
              <Separator />
              <div>
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#C85A42]" />
                  Monthly Rent Breakdown
                </h4>
                <div className="bg-muted rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex justify-between items-center p-2 bg-background rounded">
                      <span className="text-muted-foreground">Base Room Rent:</span>
                      <span className="font-semibold">{formatCurrency(lease.base_rent)}</span>
                    </div>
                    {lease.additional_fees.internet > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Wifi className="w-3 h-3 text-blue-500" /> Internet:
                        </span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.internet)}</span>
                      </div>
                    )}
                    {lease.additional_fees.water > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Droplets className="w-3 h-3 text-cyan-500" /> Water:
                        </span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.water)}</span>
                      </div>
                    )}
                    {lease.additional_fees.electricity > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Zap className="w-3 h-3 text-yellow-500" /> Electricity:
                        </span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.electricity)}</span>
                      </div>
                    )}
                    {lease.additional_fees.appliances > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Tv className="w-3 h-3 text-purple-500" /> Appliances:
                        </span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.appliances)}</span>
                      </div>
                    )}
                    {lease.additional_fees.laundry > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <WashingMachine className="w-3 h-3 text-emerald-500" /> Laundry:
                        </span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.laundry)}</span>
                      </div>
                    )}
                    {lease.additional_fees.other1_amount > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground">{lease.additional_fees.other1_name || 'Other'}:</span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.other1_amount)}</span>
                      </div>
                    )}
                    {lease.additional_fees.other2_amount > 0 && (
                      <div className="flex justify-between items-center p-2 bg-background rounded">
                        <span className="text-muted-foreground">{lease.additional_fees.other2_name || 'Other'}:</span>
                        <span className="font-medium">{formatCurrency(lease.additional_fees.other2_amount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t mt-4 pt-4 flex justify-between items-center">
                    <span className="font-semibold text-lg">Total Monthly Payment:</span>
                    <span className="text-2xl font-bold text-[#C85A42]">{formatCurrency(lease.total_monthly_rent)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Landlord Info */}
          <Separator />
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <User className="w-4 h-4" />
              Landlord Contact
            </div>
            <p className="font-semibold">{lease.landlord?.name}</p>
            <p className="text-sm text-muted-foreground">{lease.landlord?.email}</p>
            {lease.landlord?.phone && (
              <p className="text-sm text-muted-foreground">{lease.landlord.phone}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lease Terms */}
      {lease.lease_terms && (lease.lease_terms.house_rules || lease.lease_terms.payment_terms || lease.lease_terms.terms_of_service) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Lease Agreement Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={['house_rules', 'payment_terms', 'terms_of_service']} className="w-full">
              {lease.lease_terms.house_rules && (
                <AccordionItem value="house_rules">
                  <AccordionTrigger className="text-base font-semibold">
                    <span className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      House Rules and Regulations
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                      <p className="whitespace-pre-wrap text-sm">{lease.lease_terms.house_rules}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
              
              {lease.lease_terms.payment_terms && (
                <AccordionItem value="payment_terms">
                  <AccordionTrigger className="text-base font-semibold">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-emerald-500" />
                      Payment Terms
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                      <p className="whitespace-pre-wrap text-sm">{lease.lease_terms.payment_terms}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
              
              {lease.lease_terms.terms_of_service && (
                <AccordionItem value="terms_of_service">
                  <AccordionTrigger className="text-base font-semibold">
                    <span className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-500" />
                      Terms of Service
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg">
                      <p className="whitespace-pre-wrap text-sm">{lease.lease_terms.terms_of_service}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Acknowledgement Section at Bottom */}
      {((lease.status === 'pending' && !lease.tenant_acknowledged) || 
        (lease.status === 'active' && lease.force_activated && !lease.tenant_acknowledged)) && (
        <Card className="border-2 border-amber-300">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="font-semibold text-lg mb-2">Ready to Acknowledge?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                By clicking the button below, you confirm that you have read and agree to all the terms and conditions above.
                {lease.force_activated && ' Once acknowledged, the lease will be locked.'}
              </p>
              <Button
                onClick={handleAcknowledge}
                disabled={acknowledging}
                size="lg"
                className="bg-[#C85A42] hover:bg-[#B04A35]"
              >
                {acknowledging ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                I Agree to All Terms & Acknowledge Lease
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
